import React, { useState, useEffect } from 'react';
import { UserCog, X, Plus, Ban, Trash2, Loader2, Edit, Lock, Save, XCircle } from 'lucide-react';
import { usersAPI } from '../services/api';

const UsersPage = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('vendor');
  const [editingUser, setEditingUser] = useState(null);
  const [changingPassword, setChangingPassword] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordChange, setNewPasswordChange] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      const users = response.data || [];
      setUsers(users);
      console.log('✅ Users fetched successfully:', users.length);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Retry logic for production
      if (retryCount < 2) {
        console.log(`🔄 Retrying fetchUsers (attempt ${retryCount + 1})...`);
        setTimeout(() => fetchUsers(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      setStatus('خطأ في تحميل المستخدمين. يرجى تحديث الصفحة.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) {
      setStatus('يرجى إدخال اسم المستخدم وكلمة المرور');
      return;
    }

    if (newPassword.length < 6) {
      setStatus('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    try {
      setStatus('جاري إنشاء المستخدم...');
      const response = await usersAPI.create({
        username: newUsername,
        role: newRole,
        password: newPassword
      });

      // Optimistically add to list
      const newUser = {
        id: response.data.id || Date.now(),
        username: newUsername,
        role: newRole,
        is_blocked: false,
        is_removed: false,
        created_at: new Date().toISOString()
      };
      setUsers(prev => [newUser, ...prev].sort((a, b) => a.username.localeCompare(b.username)));

      setStatus(`تم إنشاء المستخدم "${newUsername}" بنجاح!`);
      setNewUsername('');
      setNewPassword('');
      setNewRole('vendor');

      setTimeout(() => {
        fetchUsers().catch(err => {
          console.error('Error refreshing users:', err);
        });
      }, 500);
    } catch (error) {
      console.error('Error creating user:', error);
      setStatus(error.response?.data?.error || 'فشل في إنشاء المستخدم');
    }
  };

  const handleBlockUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!window.confirm(`هل أنت متأكد من ${user?.is_blocked ? 'إلغاء حظر' : 'حظر'} المستخدم "${user?.username}"؟`)) {
      return;
    }

    try {
      setStatus('جاري تحديث حالة المستخدم...');
      await usersAPI.update(userId, { is_blocked: !user.is_blocked });
      
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_blocked: !u.is_blocked } : u
      ));
      
      setStatus(`تم ${user?.is_blocked ? 'إلغاء حظر' : 'حظر'} المستخدم بنجاح!`);
      
      setTimeout(() => {
        fetchUsers().catch(err => {
          console.error('Error refreshing users:', err);
        });
      }, 500);
    } catch (error) {
      console.error('Error blocking user:', error);
      setStatus(error.response?.data?.error || 'فشل في تحديث حالة المستخدم');
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!window.confirm(`هل أنت متأكد من حذف المستخدم "${user?.username}"؟\n\nسيتم حذف جميع البيانات المرتبطة بهذا المستخدم.`)) {
      return;
    }

    try {
      setStatus('جاري حذف المستخدم...');
      await usersAPI.delete(userId);
      
      setUsers(prev => prev.filter(u => u.id !== userId));
      setStatus(`تم حذف المستخدم "${user?.username}" بنجاح!`);
      
      setTimeout(() => {
        fetchUsers().catch(err => {
          console.error('Error refreshing users:', err);
        });
      }, 500);
    } catch (error) {
      console.error('Error deleting user:', error);
      setStatus(error.response?.data?.error || 'فشل في حذف المستخدم');
    }
  };

  const handleChangePassword = async (userId) => {
    if (!currentPassword || !newPasswordChange || !confirmPassword) {
      setStatus('يرجى إدخال جميع الحقول');
      return;
    }

    if (newPasswordChange.length < 6) {
      setStatus('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    if (newPasswordChange !== confirmPassword) {
      setStatus('كلمات المرور غير متطابقة');
      return;
    }

    try {
      setStatus('جاري تغيير كلمة المرور...');
      await usersAPI.changePassword(userId, currentPassword, newPasswordChange);
      
      setStatus('تم تغيير كلمة المرور بنجاح!');
      setChangingPassword(null);
      setCurrentPassword('');
      setNewPasswordChange('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      setStatus(error.response?.data?.error || 'فشل في تغيير كلمة المرور');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <UserCog className="w-8 h-8 mr-3 text-indigo-600" />
              إدارة المستخدمين
            </h1>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition"
                title="إغلاق"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {status && (
            <div className={`mb-4 p-3 rounded-lg ${
              status.includes('نجاح') || status.includes('تم')
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {status}
            </div>
          )}

          {/* Create User Form */}
          <div className="bg-indigo-50 p-6 rounded-lg mb-6 border border-indigo-200">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Plus className="w-5 h-5 mr-2 text-indigo-600" />
              إضافة مستخدم جديد
            </h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم المستخدم *
                  </label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    كلمة المرور *
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الدور *
                  </label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="vendor">بائع</option>
                    <option value="admin">مدير</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                إضافة مستخدم
              </button>
            </form>
          </div>

          {/* Users List */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <UserCog className="w-5 h-5 mr-2 text-indigo-600" />
              قائمة المستخدمين ({users.length})
            </h2>

            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                لا يوجد مستخدمون حالياً
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        اسم المستخدم
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الدور
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الحالة
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        تاريخ الإنشاء
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{user.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.role === 'admin' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {user.role === 'admin' ? 'مدير' : 'بائع'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.is_blocked ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                              محظور
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                              نشط
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.created_at 
                            ? new Date(user.created_at).toLocaleDateString('ar-SA')
                            : '-'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setChangingPassword(changingPassword === user.id ? null : user.id)}
                              className="text-indigo-600 hover:text-indigo-800"
                              title="تغيير كلمة المرور"
                            >
                              <Lock className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleBlockUser(user.id)}
                              className="text-yellow-600 hover:text-yellow-800"
                              title={user.is_blocked ? 'إلغاء الحظر' : 'حظر'}
                            >
                              <Ban className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-800"
                              title="حذف"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Change Password Modal */}
            {changingPassword && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold mb-4">تغيير كلمة المرور</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        كلمة المرور الحالية *
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        كلمة المرور الجديدة *
                      </label>
                      <input
                        type="password"
                        value={newPasswordChange}
                        onChange={(e) => setNewPasswordChange(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        required
                        minLength={6}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        تأكيد كلمة المرور *
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleChangePassword(changingPassword)}
                        className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        حفظ
                      </button>
                      <button
                        onClick={() => {
                          setChangingPassword(null);
                          setCurrentPassword('');
                          setNewPasswordChange('');
                          setConfirmPassword('');
                        }}
                        className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition flex items-center justify-center"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        إلغاء
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;

